/**
 * 需要抑制計画マスタ 需要抑制契約 編集 UI。
 *
 * 各行 = ①需要抑制BG select(区分=DR、集約 RPC 済み + エリア narrow) +
 *         ②取引先BG select(全区分、集約 RPC 済み + エリア narrow) +
 *         ③事業者 select(businessMasters) + ④契約識別番号1 text + 有効期間。
 *
 * proto SuppressionContractInput が 4 field (suppressionBgMasterId / partnerBgMasterId /
 * businessMasterId / contractId) を required で要求するため 4 つ全て必須入力。
 *
 * 区分フィルタは集約 RPC (BFF) が完了しているため、client-side では area narrow のみ行う。
 *
 * mock-app OcctoMaster.razor 1297-1368 準拠。
 */
import { useMemo } from 'react'

import { type AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import {
  type Control,
  Controller,
  type FieldValues,
  useFieldArray,
  useFormState,
  useWatch,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  Box,
  FieldRoot,
  HStack,
  Input,
  OutlineDangerButton,
  Stack,
  TertiaryButton,
  Text,
  VStack,
} from '@enechain/ecloud-designsystem'
import { FaPlusRegular } from '@enechain/ecloud-icons'

import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import { FieldErrorText } from '~/components/common/Field'
import { FormSelect } from '~/components/common/FormSelect'
import { iconSize } from '~/constants/iconSize'
import type { BusinessMasterRow } from '~/hooks/businessMaster/useListBusinessMasters'
import { type CivilDate, todayCivilDate } from '~/models/civilDate'
import { isActiveOn } from '~/models/validPeriod'
import type { BgMasterRow } from '~/pages/AdminOcctoMaster/tabs/BgMaster/hooks/useListBgMasters'
import { PeriodFields } from '~/pages/AdminOcctoMaster/tabs/_common/components/PeriodFields'
import { SubFormLabel } from '~/pages/AdminOcctoMaster/tabs/_common/components/SubFormLabel'
import {
  planMasterRowDefaultValidFrom,
  resolveExpiryFallbackLabel,
} from '~/pages/AdminOcctoMaster/tabs/_common/models/validPeriod'

type ContractEntry = {
  suppressionBgMasterId: string
  partnerBgMasterId: string
  businessMasterId: string
  contractId: string
  validFrom: { year: number; month: number; day: number }
  validTo?: { year: number; month: number; day: number }
}

type Props = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<FieldValues, any, FieldValues>
  /** i18n ページキー namespace */
  i18nNs: 'adminOcctoMaster.demandSuppressionPlanMaster'
  /**
   * 需要抑制BG 候補一覧 (集約 RPC が DR 区分でフィルタ済み)。
   * client-side では area narrow のみ行う。
   */
  suppressionBgs: BgMasterRow[]
  /**
   * 取引先BG 候補一覧 (集約 RPC が全区分を返す)。
   * client-side では area narrow のみ行う。
   */
  partnerBgs: BgMasterRow[]
  /**
   * 全 BG マスタ一覧 (削除済み判定専用、4526-plan-master-valid-period-filter
   * 設計書 §4)。 License / area / 有効期間で一切絞らない生データ。
   * 「(削除済み)」はこの一覧に存在しない場合のみ表示し、 有効期間外で
   * suppressionBgs/partnerBgs 候補から外れただけの実在 BG は
   * `{bgCode} - {bgName}` の fallback 表示を維持する (AC-06)。 list クエリ
   * 取得中は undefined (fallback 解決の「(削除済み)」誤表示防止、
   * PR #5920 レビュー指摘)。
   */
  allBgMasters: BgMasterRow[] | undefined
  /** 事業者マスタ一覧 */
  businessMasters: BusinessMasterRow[]
  /** 自身のエリアコード (同一エリアの BG のみ候補に絞り込む) */
  selfAreaCode: AreaCode | undefined
}

export const SuppressionContractsSection = ({
  control,
  i18nNs,
  suppressionBgs,
  partnerBgs,
  allBgMasters,
  businessMasters,
  selfAreaCode,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'contracts',
  })

  const rawWatchedContracts = useWatch({ control, name: 'contracts' }) as
    | ContractEntry[]
    | undefined
  const watchedContracts = rawWatchedContracts ?? []

  // BG / 事業者 select は FormSelect が内部で Controller を張るため、 error 表示用の
  // fieldState はここで一括購読する (行ごとに Controller を二重に張らない)。
  const { errors } = useFormState({ control })
  const contractErrors = errors.contracts as
    | Array<
        | {
            suppressionBgMasterId?: { message?: string }
            partnerBgMasterId?: { message?: string }
            businessMasterId?: { message?: string }
          }
        | undefined
      >
    | undefined

  // form 全体の validFrom (= レコードの適用開始日) を購読する (AC-03、
  // 4526-plan-master-valid-period-filter 設計書 §4 Q3-1)。
  const watchedRecordValidFrom = useWatch({ control, name: 'validFrom' }) as
    | CivilDate
    | undefined

  /**
   * area + 有効期間 (isActiveOn) narrow 済み 需要抑制BG 候補 (区分=DR は
   * 集約 RPC が保証済み)。 validFrom が transient に undefined の間は
   * 有効期間 narrow を skip する (fail-open)。
   */
  const suppressionBgCandidates = useMemo(
    () =>
      suppressionBgs.filter(
        (bg) =>
          (selfAreaCode === undefined || bg.areaCode === selfAreaCode) &&
          (watchedRecordValidFrom === undefined ||
            isActiveOn(bg.validFrom, bg.validTo, watchedRecordValidFrom)),
      ),
    [suppressionBgs, selfAreaCode, watchedRecordValidFrom],
  )

  /**
   * area + 有効期間 (isActiveOn) narrow 済み 取引先BG 候補 (全区分は集約 RPC
   * が保証済み)。
   */
  const partnerBgCandidates = useMemo(
    () =>
      partnerBgs.filter(
        (bg) =>
          (selfAreaCode === undefined || bg.areaCode === selfAreaCode) &&
          (watchedRecordValidFrom === undefined ||
            isActiveOn(bg.validFrom, bg.validTo, watchedRecordValidFrom)),
      ),
    [partnerBgs, selfAreaCode, watchedRecordValidFrom],
  )

  const noCandidates = suppressionBgCandidates.length === 0

  return (
    <Box>
      {/* セクションヘッダー: ラベル + 追加ボタン */}
      <HStack align="center" justify="space-between" marginBottom={2}>
        {/* design admin/occto.html `.occto-sub-label` = lg/700 (16px) が正 (#3735)。
            modal 内 H2 相当は `modalSectionTitle` を使う。 */}
        <Text textStyle="modalSectionTitle" color="Text.highEmphasis">
          {t(`page:${i18nNs}.form.contracts`)}
        </Text>
        <ButtonTooltip
          content={
            noCandidates
              ? t(`page:${i18nNs}.form.noSuppressionBgCandidates`)
              : ''
          }
        >
          <TertiaryButton
            disabled={noCandidates}
            onClick={() => {
              void append({
                suppressionBgMasterId: '',
                partnerBgMasterId: '',
                businessMasterId: '',
                contractId: '',
                validFrom: planMasterRowDefaultValidFrom(
                  watchedRecordValidFrom,
                ),
                validTo: undefined,
              })
            }}
            /* [design準拠] アイコンを付けるので size は sm 以上にする
               (design css:「.btn-xs (高さ24px) はアイコン付きに向かないので、
               アイコンを付けるなら .btn-sm 以上を使う」)。 */
            size="sm"
            title={
              noCandidates
                ? t(`page:${i18nNs}.form.noSuppressionBgCandidates`)
                : ''
            }
          >
            {/* 追加ボタンの ＋ は DS icon で描く。 従来は i18n 文言に全角の `＋ ` を
                埋めていたためフォント依存で、 同じ役割の「入札追加」 (icon) と質感が
                揃わず、 翻訳のたびに記号を書き写す必要もあった。
                design css `.btn:not(...)>svg` = 20px。 */}
            <FaPlusRegular boxSize={iconSize.labeledButton} />
            {t(`page:${i18nNs}.form.addContract`)}
          </TertiaryButton>
        </ButtonTooltip>
      </HStack>

      {/* 需要抑制契約エントリ一覧 */}
      <VStack align="stretch" gap={2}>
        {fields.length === 0 && (
          <Text color="Text.lowEmphasis" fontSize="md">
            {t(`page:${i18nNs}.form.noContractsRegistered`)}
          </Text>
        )}
        {fields.map((field, idx) => {
          const entry: ContractEntry = watchedContracts[idx] ?? {
            suppressionBgMasterId: '',
            partnerBgMasterId: '',
            businessMasterId: '',
            contractId: '',
            validFrom: todayCivilDate(),
          }

          /**
           * fallback option 用ラベル算出 (AC-MOI-023、occto-master-fk-as-of-filter
           * 設計書 Decision 2)。 対象日時点で終了していれば「（終了）」サフィックス
           * 付き、 物理削除済みなら「(削除済み)」を返す。
           */
          const fallbackOpts = {
            validTo: (bg: BgMasterRow) => bg.validTo,
            deletedLabel: t(
              'page:adminOcctoMaster.common.masterFallback.deleted',
            ),
            expiredSuffix: t(
              'page:adminOcctoMaster.common.masterFallback.expiredSuffix',
            ),
          }
          const suppressionBgFallbackLabel = resolveExpiryFallbackLabel(
            allBgMasters,
            entry.suppressionBgMasterId,
            watchedRecordValidFrom,
            {
              ...fallbackOpts,
              idOf: (bg) => bg.id,
              labelOf: (bg) => `${bg.bgCode} - ${bg.bgName}`,
            },
          )
          const partnerBgFallbackLabel = resolveExpiryFallbackLabel(
            allBgMasters,
            entry.partnerBgMasterId,
            watchedRecordValidFrom,
            {
              ...fallbackOpts,
              idOf: (bg) => bg.id,
              labelOf: (bg) => `${bg.bgCode} - ${bg.bgName}`,
            },
          )

          const suppressionBgError =
            contractErrors?.[idx]?.suppressionBgMasterId
          const partnerBgError = contractErrors?.[idx]?.partnerBgMasterId
          const businessError = contractErrors?.[idx]?.businessMasterId

          return (
            <Box
              key={field.id}
              padding={3}
              borderWidth="1px"
              borderColor="Border.lowEmphasis"
              borderRadius="md"
            >
              <Stack gap={3}>
                {/* 1行目: 需要抑制BG + 取引先BG + 削除ボタン */}
                <HStack align="flex-end" gap={3}>
                  {/* 需要抑制BG select (区分=DR + エリア) */}
                  <Box flex={1}>
                    <FieldRoot invalid={suppressionBgError !== undefined}>
                      <SubFormLabel>
                        {t(`page:${i18nNs}.form.suppressionBgLabel`)}
                      </SubFormLabel>
                      <FormSelect
                        control={control}
                        /* 既選択値が候補に無い場合のフォールバック表示。
                           絞り込み前の全 BG マスタ (allBgMasters) に存在すれば
                           `{bgCode} - {bgName}` を表示し (AC-06、有効期間外で
                           候補から外れた実在 BG)、無ければ「(削除済み)」を表示
                           する (= 物理削除された値のみ)。 未選択 ('') は
                           placeholder に戻す。 */
                        fallbackLabel={(v) =>
                          v === '' ? undefined : suppressionBgFallbackLabel
                        }
                        name={`contracts.${idx}.suppressionBgMasterId`}
                        options={suppressionBgCandidates.map((bg) => ({
                          value: bg.id,
                          label: `${bg.bgCode} - ${bg.bgName}`,
                        }))}
                        placeholder={t(`page:${i18nNs}.form.bgUnselected`)}
                      />
                      {suppressionBgError?.message !== undefined && (
                        <FieldErrorText>
                          {suppressionBgError.message}
                        </FieldErrorText>
                      )}
                    </FieldRoot>
                  </Box>

                  {/* 取引先BG select (全区分 + エリア) */}
                  <Box flex={1}>
                    <FieldRoot invalid={partnerBgError !== undefined}>
                      <SubFormLabel>
                        {t(`page:${i18nNs}.form.contractPartnerBgLabel`)}
                      </SubFormLabel>
                      <FormSelect
                        control={control}
                        /* AC-06 rework: 上の 需要抑制BG select と同ルール。 */
                        fallbackLabel={(v) =>
                          v === '' ? undefined : partnerBgFallbackLabel
                        }
                        name={`contracts.${idx}.partnerBgMasterId`}
                        options={partnerBgCandidates.map((bg) => ({
                          value: bg.id,
                          label: `${bg.bgCode} - ${bg.bgName}`,
                        }))}
                        placeholder={t(`page:${i18nNs}.form.bgUnselected`)}
                      />
                      {partnerBgError?.message !== undefined && (
                        <FieldErrorText>
                          {partnerBgError.message}
                        </FieldErrorText>
                      )}
                    </FieldRoot>
                  </Box>

                  {/* 削除ボタン */}
                  <OutlineDangerButton
                    flexShrink={0}
                    aria-label={t(`page:${i18nNs}.form.removeContract`)}
                    onClick={() => {
                      remove(idx)
                    }}
                    size="sm"
                  >
                    {t(`page:${i18nNs}.form.removeContract`)}
                  </OutlineDangerButton>
                </HStack>

                {/* 2行目: 事業者 + 契約識別番号1 */}
                <HStack align="flex-end" gap={3}>
                  {/* 事業者 select */}
                  <Box flex={1}>
                    <FieldRoot invalid={businessError !== undefined}>
                      <SubFormLabel>
                        {t(`page:${i18nNs}.form.businessLabel`)}
                      </SubFormLabel>
                      <FormSelect
                        control={control}
                        // 候補に無い既存値は native select 時代と同じく ID をそのまま
                        // 表示する (選択肢には出さない)。 未選択 ('') は placeholder。
                        fallbackLabel={(v) => (v === '' ? undefined : v)}
                        name={`contracts.${idx}.businessMasterId`}
                        options={businessMasters.map((b) => ({
                          value: b.id,
                          label: `${b.businessCode} - ${b.businessName}`,
                        }))}
                        placeholder={t(
                          `page:${i18nNs}.form.businessUnselected`,
                        )}
                      />
                      {businessError?.message !== undefined && (
                        <FieldErrorText>{businessError.message}</FieldErrorText>
                      )}
                    </FieldRoot>
                  </Box>

                  {/* 契約識別番号1 text */}
                  <Box flex={1}>
                    <Controller
                      control={control}
                      name={`contracts.${idx}.contractId`}
                      render={({ field: f, fieldState }) => (
                        <FieldRoot invalid={fieldState.error !== undefined}>
                          <SubFormLabel>
                            {t(`page:${i18nNs}.form.contractIdLabel`)}
                          </SubFormLabel>
                          <Input
                            ref={f.ref}
                            onBlur={f.onBlur}
                            onChange={f.onChange}
                            placeholder={t(
                              `page:${i18nNs}.form.contractIdPlaceholder`,
                            )}
                            value={f.value as string}
                          />
                          {fieldState.error?.message !== undefined && (
                            <FieldErrorText>
                              {fieldState.error.message}
                            </FieldErrorText>
                          )}
                        </FieldRoot>
                      )}
                    />
                  </Box>

                  {/* flex balance */}
                  <Box flexShrink={0} width="64px" />
                </HStack>

                {/* 適用期間 */}
                <PeriodFields
                  basePath={`contracts.${idx}`}
                  clearAsNull
                  control={control}
                  i18nNs={i18nNs}
                />
              </Stack>
            </Box>
          )
        })}
      </VStack>
    </Box>
  )
}
